import type { Table } from "dexie";
import {
  bumpSyncMeta,
  estimateJsonInputSize,
  measurePerformanceAsync,
  tombstone,
  type Entity,
  type Repository,
} from "@roqeet/core";
import { getDeviceOrigin } from "./origin.js";

/**
 * Generic Dexie-backed repository (decision #3) that transparently maintains
 * sync metadata (decision #20):
 *  - every `put` recomputes rev/updatedAt/hash via core's `bumpSyncMeta`,
 *  - `delete` writes a tombstone (soft delete) so deletions propagate in sync,
 *  - reads filter out tombstones so the UI never sees deleted rows.
 *
 * Keeping this logic in one place means the core's storage contract is honored
 * identically for every entity kind without per-entity boilerplate.
 */
export class DexieRepository<T extends Entity> implements Repository<T> {
  constructor(private readonly table: Table<T, string>) {}

  private isLive(value: T | undefined): value is T {
    return !!value && !value.sync.deletedAt;
  }

  async get(id: string): Promise<T | undefined> {
    const value = await this.table.get(id);
    return this.isLive(value) ? value : undefined;
  }

  async getMany(ids: string[]): Promise<(T | undefined)[]> {
    const values = await this.table.bulkGet(ids);
    return values.map((v) => (this.isLive(v) ? v : undefined));
  }

  async list(): Promise<T[]> {
    const all = await this.table.toArray();
    return all.filter((v) => this.isLive(v));
  }

  async put(value: T): Promise<void> {
    await measurePerformanceAsync(
      "save_project_state",
      {
        inputSize: estimateJsonInputSize(value),
        fileId: this.table.name,
        packageId: value.id,
        cache: "miss",
        thread: "main",
        invalidationReason: "entity_put",
      },
      async () => {
        const origin = getDeviceOrigin();
        const next = { ...value, sync: await bumpSyncMeta(value, origin) } as T;
        await this.table.put(next);
      },
    );
  }

  async putMany(values: T[]): Promise<void> {
    await measurePerformanceAsync(
      "save_project_state",
      {
        inputSize: estimateJsonInputSize(values),
        fileId: this.table.name,
        packageId: values.map((v) => v.id).join(","),
        cache: "miss",
        thread: "main",
        invalidationReason: "entity_bulk_put",
        detail: { count: values.length },
      },
      async () => {
        const origin = getDeviceOrigin();
        const next = await Promise.all(
          values.map(async (v) => ({ ...v, sync: await bumpSyncMeta(v, origin) }) as T),
        );
        await this.table.bulkPut(next);
      },
    );
  }

  async delete(id: string): Promise<void> {
    const existing = await this.table.get(id);
    if (!existing) return;
    // Soft-delete: keep the row as a tombstone for sync (decision #20).
    const dead = {
      ...existing,
      sync: tombstone(existing.sync, getDeviceOrigin()),
    } as T;
    await this.table.put(dead);
  }

  async count(): Promise<number> {
    // Count only live rows. Cheap enough for the small tables this is used on.
    return this.table.filter((v) => !v.sync.deletedAt).count();
  }
}
